import {API} from 'aws-amplify'
import {apiCallWithAuthCheck} from '../auth/core/AuthErrorInterceptor'
import {normalizePins, Pin} from './pins'

/**
 * Client for `GET/PUT /v1/me/pins` (JobService, behind the same authorizer as the other `/v1`
 * calls). Open to every signed-in user, not only the shared-folders pilot; API keys get 404.
 */
const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'
const PINS_ROUTE = 'v1/me/pins'

export interface PinsResponse {
  /** In display order. */
  pins: Pin[]
  /** When the list last changed, or null when the user never pinned anything. */
  updatedAt: string | null
}

/** The user's pins, and whether pinning works for them at all. */
export interface PinsState extends PinsResponse {
  /**
   * False when the list could not be read: the endpoint is not deployed (404), the network failed,
   * or the answer made no sense. The browser then shows no pins and offers no Pin actions.
   */
  available: boolean
}

export const PINS_UNAVAILABLE: PinsState = {available: false, pins: [], updatedAt: null}

const toResponse = (data: unknown): PinsResponse | null => {
  const body = data as {pins?: unknown; updatedAt?: unknown} | null | undefined
  if (!body || !Array.isArray(body.pins)) {
    return null
  }
  return {
    pins: normalizePins(body.pins),
    updatedAt: typeof body.updatedAt === 'string' ? body.updatedAt : null,
  }
}

/**
 * `GET /v1/me/pins`. Never throws: any failure reads as "pinning unavailable", so a missing or broken
 * endpoint can never break the Files browser. Deliberately not wrapped in apiCallWithAuthCheck, like
 * `getSharingFeatures`: a background read must never be what opens the session-expired dialog (the
 * listing beside it does that when the session really expired).
 */
export async function getPins(): Promise<PinsState> {
  try {
    const response = toResponse(await API.get(apiName, PINS_ROUTE, {}))
    return response ? {available: true, ...response} : PINS_UNAVAILABLE
  } catch {
    return PINS_UNAVAILABLE
  }
}

/** The server's reason for refusing a change (400 `{code: 'bad_request', message}`). */
export function pinsErrorMessage(error: unknown): string {
  const response = (error as {response?: {status?: unknown; data?: {message?: unknown}}})?.response
  if (typeof response?.data?.message === 'string' && response.data.message) {
    return response.data.message
  }
  if (response?.status === 404) {
    return 'Pinning is not available right now.'
  }
  return 'Something went wrong. Try again.'
}

/**
 * `PUT /v1/me/pins`: replaces the whole list (at most 50; the server drops repeats) and answers with
 * the list it kept. A user action, so an expired session opens the sign-in dialog as anywhere else.
 */
export async function putPins(pins: Pin[]): Promise<PinsResponse> {
  const data = await apiCallWithAuthCheck(() => API.put(apiName, PINS_ROUTE, {body: {pins}}))
  return toResponse(data) ?? {pins, updatedAt: null}
}
